import React, { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import CloseOutlinedIcon from 'components/material-symbol/icons/Close'
import EventNoteOutlinedIcon from 'components/material-symbol/icons/EventNote'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import PeopleOutlinedIcon from 'components/material-symbol/icons/People'
import MonetizationOnOutlinedIcon from 'components/material-symbol/icons/MonetizationOn'
import AttachMoneyOutlinedIcon from 'components/material-symbol/icons/AttachMoney'
import ReceiptOutlinedIcon from 'components/material-symbol/icons/Receipt'
import { TonalButton } from 'components/empty-page-state'
import { currency } from 'lib/utils/string'
import { centsToDollars } from 'lib/utils/number'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import {
  toFullTextDate,
  getTimePeriodWithTimezone,
  toFullTextDayDateWithTimezone,
} from 'utils/dateUtils'
import useIconAccessor from 'components/lesson-type-category/icons'
import IconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/icon-badge'
import EnrollmentItem from './enrollment-item'
import { useStyles } from './styles'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import PaymentActions from './payment-actions'
import SessionActions from './session-actions'
import SessionNotesSection from './session-notes-section'
import EditSessionSubview from './edit-session-subview'
import CancelBookingDialog from '../cancel-booking-dialog'
import RequestPaymentDialog from 'components/request-payment-dialog'
import RemindOrCancelRequestPaymentDialog from '../remind-or-cancel-request-payment-dialog'
import useIsMobile from 'lib/hooks/use-is-mobile'
import { Elements } from '@stripe/react-stripe-js'
import { stripePromise } from 'lib/stripe'
import PaymentsUnavailableWarning from './payments-unavailable-warning'
import EventBusyOutlinedIcon from 'components/material-symbol/icons/EventBusy'
import {
  checkSessionStatus,
  getLessonPackReceiptIdFromEnrollments,
} from 'lib/sessions/utils'
import { flags, useFeatureFlags } from 'lib/feature-flags'

const getHeroDateLabel = (startDateTime, timezone) => {
  const start = DateTime.fromISO(startDateTime, { zone: timezone })
  return start.isValid ? start.toFormat('MMM dd').toUpperCase() : null
}

const SessionDetailsDialogContent = ({
  booking,
  onReschedule,
  onPayment,
  onCancellation,
  closeDialog,
  startInNotesPanel,
}) => {
  const {
    title,
    startDateTime,
    endDateTime,
    coachFacility,
    enrollments,
    isCanceled,
    lessonType,
    timezone,
    lessonType: { paymentAccount, id: lessonTypeId },
  } = booking
  const { deletedAt, chargesEnabled } = paymentAccount || {}
  const isPaymentAccountValid = paymentAccount && !deletedAt && chargesEnabled

  const [isCancelling, setIsCancelling] = useState(false)
  const isMobile = useIsMobile()

  const { isPaid, isLessonCredit } = checkSessionStatus({
    isCanceled,
    enrollments,
  })
  const hasPaymentAccount = !!paymentAccount
  const isUpcoming =
    !isCanceled &&
    !!endDateTime &&
    DateTime.now() < DateTime.fromISO(endDateTime)
  const statusLabel = isCanceled ? 'Canceled' : isUpcoming ? 'Upcoming' : null
  const heroDateLabel = getHeroDateLabel(startDateTime, timezone)

  const facilityAddress = [
    coachFacility?.city && `${coachFacility.city},`,
    coachFacility?.state,
    coachFacility?.zipCode,
  ]
    .filter(s => !!s)
    .join(' ')

  const enrollment = enrollments[0]
  const attendeeEnrollments = (enrollments || []).filter(item => item?.student)
  const { confirmationCode } = enrollment

  const paymentAmount = useMemo(() => {
    if (isLessonCredit) {
      return enrollment?.lessonCredit?.lessonPack?.lessonPackOffering
        ?.pricePerLessonInCents
    } else {
      return (isPaid && enrollment?.transactions[0]?.amountInCents) || 0
    }
  }, [isLessonCredit, isPaid, enrollment])

  const paymentDate = useMemo(() => {
    if (isLessonCredit) {
      return enrollment?.createdAt
    } else {
      return (isPaid && enrollment?.transactions[0]?.createdAt) || ''
    }
  }, [isLessonCredit, isPaid, enrollment])

  const registrationPaymentId =
    isPaid && enrollment?.transactions[0]?.registrationPayment.id
  const lessonPackReceiptId = getLessonPackReceiptIdFromEnrollments(enrollments)
  const student = enrollment?.student
  const studentName = `${student?.firstName} ${student.lastName}`
  const isPrivateSession = booking.__typename === 'Session'
  const isPaymentRequestVisible =
    !isPaid &&
    !isCanceled &&
    !isLessonCredit &&
    hasPaymentAccount &&
    lessonType.priceInCents > 0 &&
    isPaymentAccountValid

  const canSendPaymentRequestReminder =
    isPaymentRequestVisible && !enrollment?.paymentRequestedDate

  const classes = useStyles()
  const accessor = useIconAccessor()
  const categoryText = accessor[lessonType.category]?.text
  const [isCoachNotesEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_NOTES])
  const showNotes =
    isCoachNotesEnabled && isPrivateSession && student?.id && booking?.id
  const [notesPanelOpen, setNotesPanelOpen] = useState(startInNotesPanel)
  const [view, setView] = useState('detail')
  const notesSubviewOpen = Boolean(
    showNotes && notesPanelOpen && view === 'detail',
  )

  const handleNotesPanelChange = open => {
    setNotesPanelOpen(open)
  }

  const openEditSession = () => {
    setNotesPanelOpen(false)
    setView('edit')
  }

  const closeEditSession = () => setView('detail')

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <Box className={classes.actionButtonContainer}>
            <Avatar
              className={classes.actionAvatar}
              aria-label="Close"
              onClick={closeDialog}
            >
              <CloseOutlinedIcon color="primary" />
            </Avatar>
          </Box>
          <Box className={classes.heroContent}>
            {statusLabel ? (
              <Chip
                label={statusLabel}
                size="small"
                className={classes.statusChip}
              />
            ) : null}
            <Typography className={classes.heroTitle}>{title}</Typography>
            {categoryText ? (
              <Typography className={classes.heroSubtitle}>
                {categoryText}
              </Typography>
            ) : null}
          </Box>
          {heroDateLabel ? (
            <Box className={classes.heroMeta}>
              <Typography className={classes.heroMetaLabel}>When</Typography>
              <Typography className={classes.heroMetaValue}>
                {heroDateLabel}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        {view === 'edit' ? (
          <EditSessionSubview
            booking={booking}
            onSuccess={onReschedule}
            onBack={closeEditSession}
          />
        ) : null}
        {view === 'detail' && !notesSubviewOpen ? (
          <Box className={classes.detailsCard}>
            <Typography className={classes.sectionLabel}>
              Session details
            </Typography>
            {coachFacility ? (
              <Box className={classes.detailRow}>
                <IconBadge icon={RoomOutlinedIcon} />
                <Box minWidth={0}>
                  <Typography className={classes.detailPrimary}>
                    {coachFacility.name}
                  </Typography>
                  <Typography className={classes.detailSecondary}>
                    {facilityAddress}
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <Box className={classes.detailRow}>
              <IconBadge icon={EventNoteOutlinedIcon} />
              <Box className={classes.detailRowContent}>
                <Box className={classes.detailCopy}>
                  <Typography className={classes.detailPrimary}>
                    {toFullTextDayDateWithTimezone(startDateTime, timezone)}
                  </Typography>
                  <Typography className={classes.detailSecondary}>
                    {getTimePeriodWithTimezone(
                      startDateTime,
                      endDateTime,
                      timezone,
                    )}
                  </Typography>
                </Box>
                <SessionActions
                  booking={booking}
                  onEditSession={openEditSession}
                />
              </Box>
            </Box>
            <Box className={classes.detailRow}>
              <IconBadge icon={MonetizationOnOutlinedIcon} />
              <Box className={classes.detailRowContent}>
                {isLessonCredit ? (
                  <>
                    <Box className={classes.detailCopy}>
                      <Typography className={classes.detailPrimary}>
                        Credit Redeemed
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        Redeemed on {toFullTextDate(paymentDate)}
                      </Typography>
                    </Box>
                    {lessonPackReceiptId ? (
                      <SensitiveInfoWrapper>
                        <TonalButton
                          className={classes.markAsPaidButton}
                          startIcon={
                            <ReceiptOutlinedIcon fontSize="small" fill={0} />
                          }
                          to={`/account/payments/transactions/${lessonPackReceiptId}`}
                          data-testid="view-original-transaction"
                        >
                          View Transaction
                        </TonalButton>
                      </SensitiveInfoWrapper>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Typography className={classes.detailPrimary}>
                      {isPaid
                        ? `${currency(
                          centsToDollars(paymentAmount),
                        )} on ${toFullTextDate(paymentDate)}`
                        : 'Pending Payment'}
                    </Typography>
                    <SensitiveInfoWrapper>
                      {isPaid ? (
                        <PaymentActions
                          registrationPaymentId={registrationPaymentId}
                          enrollment={enrollment}
                          eventTitle={title}
                          eventDate={startDateTime}
                          participantName={studentName}
                          lessonType={lessonType}
                          onPaymentSuccess={onPayment}
                        />
                      ) : !isCanceled ? (
                        <RecordBookingPaymentDialog
                          trigger={
                            <TonalButton
                              className={classes.markAsPaidButton}
                              startIcon={
                                <AttachMoneyOutlinedIcon
                                  fontSize="small"
                                  fill={0}
                                />
                              }
                              data-testid="mark-as-paid"
                            >
                              Mark As Paid
                            </TonalButton>
                          }
                          enrollment={enrollment}
                          eventTitle={title}
                          eventDate={startDateTime}
                          participantName={studentName}
                          defaultAmountInCents={lessonType.priceInCents}
                          acceptOnlinePayments={
                            lessonType.acceptsOnlinePayments
                          }
                          onPaymentSuccess={onPayment}
                        />
                      ) : null}
                    </SensitiveInfoWrapper>
                  </>
                )}
              </Box>
            </Box>
            {attendeeEnrollments.map(item => (
              <Box key={item.id} className={classes.detailRow}>
                <IconBadge icon={PeopleOutlinedIcon} />
                <EnrollmentItem enrollment={item} closeDialog={closeDialog} />
              </Box>
            ))}
          </Box>
        ) : null}
        {view === 'detail' && showNotes ? (
          <SessionNotesSection
            sessionId={booking.id}
            studentId={student.id}
            studentFirstName={student.firstName}
            sessionTitle={title}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            timezone={timezone}
            startInPanel={Boolean(showNotes && startInNotesPanel)}
            onPanelChange={handleNotesPanelChange}
          />
        ) : null}
        {view === 'detail' && !notesSubviewOpen ? (
          <>
            {isPaymentRequestVisible && (
              <Box className={classes.callout}>
                {canSendPaymentRequestReminder ? (
                  <>
                    <Box display="flex" alignItems="center" width="60%">
                      <Typography
                        variant="body2"
                        gutterBottom
                        color="textPrimary"
                        data-testid="payment-request-reminder-text"
                      >
                        Ready to collect? Click here to collect in person or
                        email your student a link for easy, online payment.
                      </Typography>
                    </Box>
                    <Box color="textSecondary">
                      <Elements stripe={stripePromise}>
                        <RequestPaymentDialog
                          trigger={
                            <Button
                              color="primary"
                              variant="outlined"
                              disabled={isCancelling}
                              data-testid="collect-payment"
                            >
                              Collect Payment
                            </Button>
                          }
                          onPaymentSuccess={() => {
                            closeDialog()
                            onPayment()
                          }}
                          confirmationCode={confirmationCode}
                          enrollment={{
                            category: lessonType.category,
                            priceInCents: lessonType.priceInCents,
                            startDateTime,
                            endDateTime,
                            timezone,
                            title,
                            ...enrollment,
                          }}
                        />
                      </Elements>
                    </Box>
                  </>
                ) : (
                  <Grid container alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textPrimary">
                        Still waiting on a payment? Remind your student to
                        submit their payment online
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} color="textSecondary">
                      <Grid
                        container
                        alignItems="center"
                        justifyContent={isMobile ? 'center' : 'flex-end'}
                        className={classes.cancelPaymentRequestButtons}
                      >
                        <Grid item>
                          <RemindOrCancelRequestPaymentDialog
                            trigger={<Button color="primary">Cancel</Button>}
                            onSuccess={onCancellation}
                            confirmationCode={confirmationCode}
                            enrollmentId={enrollment.id}
                            isCancelReminder={true}
                          />
                        </Grid>
                        <Grid item>
                          <RemindOrCancelRequestPaymentDialog
                            trigger={
                              <Button color="primary" variant="outlined">
                                Remind
                              </Button>
                            }
                            onSuccess={onCancellation}
                            confirmationCode={confirmationCode}
                            enrollmentId={enrollment.id}
                            isCancelReminder={false}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
            {hasPaymentAccount && !isPaymentAccountValid && (
              <PaymentsUnavailableWarning lessonTypeId={lessonTypeId} />
            )}
            {!isCanceled && (
              <Box className={classes.cancelCard}>
                <Box className={classes.cancelCardHeader}>
                  <Typography
                    className={classes.cancelCardTitle}
                    component="h2"
                  >
                    Cancel Session
                  </Typography>
                </Box>
                <Box className={classes.cancelCardBody}>
                  <Typography className={classes.cancelCardText}>
                    All students will be notified. Refunds are not issued
                    automatically. If needed, you can issue a refund from the
                    student&apos;s Transaction Details.
                  </Typography>
                  <CancelBookingDialog
                    trigger={
                      <Button
                        startIcon={<EventBusyOutlinedIcon fill={0} />}
                        variant="outlined"
                        color="primary"
                        className={classes.cancelActionButton}
                        data-testid="cancel-session"
                      >
                        Cancel
                      </Button>
                    }
                    onConfirm={() => setIsCancelling(true)}
                    onSuccess={onCancellation}
                    confirmationCode={confirmationCode}
                  />
                </Box>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
    </>
  )
}

const SessionDetailsDialog = ({
  isOpen,
  setIsOpen,
  booking,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const handleClose = () => setIsOpen(false)

  return (
    <Dialog
      open={!!isOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      classes={isMobile ? undefined : { paper: classes.dialogPaper }}
    >
      {isOpen ? (
        <SessionDetailsDialogContent
          booking={booking}
          onReschedule={onReschedule}
          onPayment={onPayment}
          onCancellation={onCancellation}
          closeDialog={handleClose}
          startInNotesPanel={isOpen === 'notes'}
        />
      ) : null}
    </Dialog>
  )
}

export default SessionDetailsDialog
